// Phase-02-deferred hooks (they need the StatsFilterKit engine, ported in Phase 04).
// Thin wrappers over the pure ranking layer + a synchronous snapshot read today; at the
// Track-2 cutover only these bodies change (fetch a leaderboard endpoint and adapt to
// the same ProtoRankingEntry shape).
//
// `useRankingBoard` is what the widget reads: the snapshot path is the hooks below, unchanged;
// with ?live=1 the same board comes from the shared group (`useLiveGroupFile` — her league's
// Stats file), ranked by Ben's own computeRanking (`live/ranking.ts`).

import { chipIsOn } from "../engines/stats-filter-kit.js";
import { leagueOptionInfo } from "../modules/CountryFlag.js";
import type { OptionInfo } from "../modules/FilterPopovers.js";
import { useMemo } from "react";
import type { ProtoRankingEntry } from "@catbento/shared";
import { readSnapshot } from "../data/snapshot.js";
import { getMetricCategories } from "../data/stats.js";
import { playerById, playerNameOf, type PlayerId } from "../data/players.js";
import {
  findRankingMetric,
  firstRankingMetricKey,
  rankingSeasonCodes,
  computeRanking,
  type RankingMetricResult,
} from "../data/rankings.js";
import { LIVE_RANKING_CHIPS, LIVE_RANKING_RANGE_FILTERS, RANKING_RANGE_FILTERS } from "../modules/rankings-view.js";
import type {
  ActiveFilters,
  CategoricalActive,
  KitPlayer,
  KitMetric,
  RangeActive,
  RangeFilterConfig,
} from "../engines/stats-filter-kit.js";
import { useFilterOptions } from "./useFilterOptions.js";
import { LIVE } from "../live/flag.js";
import { distinctPlayers } from "../live/group-players.js";
import { liveRanking } from "../live/ranking.js";
import { chipsWithData, MINUTES_CHIP_DEFAULT } from "../live/stats-files.js";
import {
  chipOptionsOfGroups,
  filesOfGroups,
  groupsKey,
  playersOfGroups,
  refPlayerOf,
  useLiveLeagueGroups,
} from "./useLeagueGroups.js";
import { liveStatsCategory } from "../live/stats.js";

const NO_REF: KitPlayer = {};

/** The categories a Ranking picks from: on ?live=1 the Stats tiles' own (Goalkeeping's two new
 *  claim rows, Q-015 B), so a row dragged out of a live Goalkeeping tile finds its metric. */
function rankingCategories() {
  const categories = getMetricCategories();
  return LIVE ? categories.map(liveStatsCategory) : categories;
}

/** The category a stat belongs to (a Ranking opens on category + stat), or null. */
export function rankingCategoryOf(metricKey: string): string | null {
  for (const c of rankingCategories()) {
    if (c.subcategories.some((sub) => sub.playerMetrics.some((m) => m.key === metricKey))) return c.id;
  }
  return null;
}

/** Every season code with real data across the population, newest-first. */
export function useRankingSeasonCodes(): string[] {
  return useMemo(() => rankingSeasonCodes(readSnapshot().players), []);
}

/** Resolve (categoryId, metricKey) → metric + category, or null. */
export function useRankingMetric(
  categoryId: string | null,
  metricKey: string | null,
): RankingMetricResult | null {
  return useMemo(
    () => findRankingMetric(rankingCategories(), categoryId, metricKey),
    [categoryId, metricKey],
  );
}

/** A category's first metric key — the default a "… Rank" tile opens on. */
export function useFirstRankingMetricKey(categoryId: string | null): string | null {
  return useMemo(() => firstRankingMetricKey(rankingCategories(), categoryId), [categoryId]);
}

export interface UseRankingParams {
  metric: KitMetric | null;
  seasonCode: string;
  useTotal: boolean;
  active: ActiveFilters;
  refPlayerName: string | null;
}

/** The ranked leaderboard for the current metric/season/mode/filters. Empty when no
 *  metric is resolved. `active` is depended on by reference, so callers pass a fresh
 *  object when the filter set changes (04g-3b). */
export function useRanking({ metric, seasonCode, useTotal, active, refPlayerName }: UseRankingParams): ProtoRankingEntry[] {
  return useMemo(() => {
    if (!metric) return [];
    const players = readSnapshot().players;
    const refPlayer = (refPlayerName && players[refPlayerName]) as unknown as KitPlayer;
    return computeRanking(players, metric, seasonCode, useTotal, active, refPlayer ?? NO_REF, RANKING_RANGE_FILTERS);
  }, [metric, seasonCode, useTotal, active, refPlayerName]);
}

// --- the widget's board (snapshot or live) ---------------------------------------------

export interface RankingBoardParams {
  /** The player the Ranking was made for (`_rankingSourcePlayer`). Live: her league is the group. */
  sourcePlayer: PlayerId | null;
  /** Live: the user removed the League chip (✕) — empty until a league is picked. */
  leagueCleared?: boolean;
  metric: KitMetric | null;
  /** The season picked (`_rankingSeason`); null or one without data → the default. */
  season: string | null;
  useTotal: boolean;
  active: ActiveFilters;
  /** Live: the Men's / Women's switch's pick (null = the player's gender). */
  womensPick?: boolean | null;
}

/** What the chip bar needs (`FilterChipBar`'s props). The optional ones are live only. */
export interface RankingChips {
  /** Live only: each value's heading + icon in its list (the League chip: country + flag). */
  optionInfo?: Record<string, (value: string) => OptionInfo | undefined>;
  options: Record<string, string[]>;
  refPlayer: KitPlayer;
  rangeFilters: RangeFilterConfig;
  addMenuKeys?: readonly string[];
  newChipValue?: (key: string) => CategoricalActive | RangeActive | undefined;
}

export interface RankingBoard {
  seasonCodes: string[];
  /** The season shown. */
  season: string | null;
  entries: ProtoRankingEntry[];
  /** "N players ranked" (distinct players); null = nothing to rank (live) — `label` says why. */
  count: number | null;
  /** The readout's words; undefined = the widget's default ("players ranked"). */
  label?: string;
  chips: RankingChips;
  /** The ranked players' gender (a league is one gender) — a dragged row notes it, so a live
   *  Stats tile can take her. Null = no file shown (no rows either). */
  womens: boolean | null;
  /** Live only: the League chip's default — the canvas player's league (Ben, 2026-10-16). */
  defaultLeague?: string;
  /** Live only: a league's chip label by its competition id (the search finds anyone's league). */
  leagueOf?: (competitionId: number) => string | undefined;
  /** Live only: the gender shown and the player's own (the Men's / Women's switch). */
  gender?: { shown: boolean | null; player: boolean | null };
  /** Live only: a league's gender by its chip label. */
  womensOfLeague?: (label: string) => boolean | null;
}

function useSnapshotRankingBoard({ sourcePlayer, metric, season, useTotal, active }: RankingBoardParams): RankingBoard {
  const seasonCodes = useRankingSeasonCodes();
  const shown = season && seasonCodes.includes(season) ? season : (seasonCodes[0] ?? null);
  // The ranking hook still takes the snapshot name (it goes by id in its own Track 2 slice).
  const sourcePlayerName = playerNameOf(readSnapshot().players, sourcePlayer);
  const entries = useRanking({ metric, seasonCode: shown ?? "", useTotal, active, refPlayerName: sourcePlayerName });
  const options = useFilterOptions();
  const refPlayer = useMemo<KitPlayer>(
    () => (playerById(readSnapshot().players, sourcePlayer) ?? {}) as unknown as KitPlayer,
    [sourcePlayer],
  );
  return {
    seasonCodes,
    season: shown,
    entries,
    count: entries.length,
    chips: { options, refPlayer, rangeFilters: RANKING_RANGE_FILTERS },
    // The snapshot holds women's leagues only (and the snapshot site has no gender rule).
    womens: true,
  };
}

// A League chip added from "+ Add filter" starts with nothing ticked (Ben, 2026-10-16: not the
// player's league, which a first tick would then add to). Minutes starts at 150+ (rules.md).
const liveNewChipValue = (key: string) =>
  key === "league" ? [] : key === "minutes" ? { ...MINUTES_CHIP_DEFAULT } : undefined;

function useLiveRankingBoard({ sourcePlayer, metric, season, useTotal, active, leagueCleared, womensPick = null }: RankingBoardParams): RankingBoard {
  // Live always has one: the module falls back to the snapshot's first player (a real DB id).
  if (sourcePlayer === null) throw new Error("A live Ranking needs a player to take its league from");
  // The League chip's leagues (useLeagueGroups: the canvas player's by default, tick others).
  const lg = useLiveLeagueGroups(sourcePlayer, season, active.league as CategoricalActive | undefined, leagueCleared, womensPick);
  const key = groupsKey(lg);
  const byPosition = chipIsOn(active.position);

  const refPlayer = useMemo<KitPlayer>(() => refPlayerOf(lg, sourcePlayer), [lg.home, sourcePlayer]);
  const options = useMemo(() => chipOptionsOfGroups(lg), [key, lg.leagueOptions]);
  const entries = useMemo(() => {
    if (lg.problem !== null || !metric) return [];
    const players = playersOfGroups(lg, byPosition ? "position" : "total", [metric.key]);
    return liveRanking(players, metric, lg.shown, useTotal, active, refPlayer, byPosition);
  }, [lg.problem, key, metric, byPosition, useTotal, active, refPlayer, lg.shown]);

  const chips: RankingChips = {
    options,
    refPlayer,
    rangeFilters: LIVE_RANKING_RANGE_FILTERS,
    // Q-013: only the chips at least half the players (with minutes) have data for.
    addMenuKeys: chipsWithData(LIVE_RANKING_CHIPS, filesOfGroups(lg)),
    newChipValue: liveNewChipValue,
    optionInfo: { league: leagueOptionInfo(lg.leagues) },
  };
  const base = {
    seasonCodes: lg.seasonCodes,
    season: lg.shown,
    chips,
    defaultLeague: lg.defaultLeague,
    leagueOf: lg.leagueOf,
    gender: { shown: lg.womens, player: lg.playerWomens },
    womensOfLeague: lg.womensOfLeague,
  };
  if (lg.problem !== null) {
    // ✕ on the League chip: ask for a league (or a player — the search finds anyone).
    const label = lg.cleared ? "Pick a league, or find a player" : `No ranking: ${lg.problem}`;
    return { ...base, entries: [], count: null, label, womens: null };
  }
  return {
    ...base,
    entries,
    count: distinctPlayers(entries),
    // The League chip says which league(s); a picked league with no file this season is named.
    label: lg.missingNote ? `players ranked · ${lg.missingNote}` : "players ranked",
    womens: lg.womens,
  };
}

/** The Ranking widget's board: the snapshot's, or with ?live=1 her league's Stats file. */
export const useRankingBoard: (p: RankingBoardParams) => RankingBoard = LIVE ? useLiveRankingBoard : useSnapshotRankingBoard;
